import React from "react";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { chipsOf } from "./signalMeta";

// The small pieces a hiring intel entry is drawn with, shared by the feed row and the tracker table.

/** A quiet line of chips, at most `max` and "+N" for the rest. */
export const Chips = ({ items, max = 5, kind = "" }) => {
  const { shown, more } = chipsOf(items, max);
  if (!shown.length) return null;
  return (
    <span className={`sg-hchips ${kind}`}>
      {shown.map(t => <span key={t} className="sg-hchip">{t}</span>)}
      {more > 0 && <span className="sg-hchip sg-hchip--more">+{more}</span>}
    </span>
  );
};

/** Who is hiring: their photo (or initials), name, title and a "Hiring" tag. */
export const OwnerBlock = ({ owner }) => (
  <span className="sg-howner">
    <ContactAvatar photo={owner.photoUrl} name={owner.name} />
    <span className="sg-howner__text">
      <span className="sg-howner__name">{owner.name}</span>
      {owner.title && <span className="sg-howner__title">{owner.title}</span>}
    </span>
    <span className="sg-htag">Hiring</span>
  </span>
);

const shortDate = v => (v && !isNaN(new Date(v)) ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

/** One role from a hiring post: title, team and seniority, its tech, when it was posted, who owns it, the post. */
export const RoleLine = ({ role }) => (
  <div className="sg-hrole">
    <div className="sg-hrole__main">
      <span className="sg-hrole__title">{role.title || "Open role"}</span>
      {[role.team, role.seniority, role.location].filter(Boolean).length > 0 && (
        <span className="sg-hrole__sub">{[role.team, role.seniority, role.location].filter(Boolean).join(" · ")}</span>
      )}
    </div>
    <Chips items={role.technologies} max={4} />
    <span className="sg-hrole__when">{shortDate(role.postedAt)}</span>
    <span className="sg-hrole__owner">{(role.owner && role.owner.name) || ""}</span>
    {role.postUrl ? (
      <a className="sg-link" href={role.postUrl} target="_blank" rel="noopener noreferrer">View post</a>
    ) : <span />}
  </div>
);

/** Why this hiring matters to what you sell (for a competitor: what it tells you), then a quiet suggestion. */
export const WhyItMatters = ({ why, competitor = false }) =>
  why ? (
    <div className="sg-hwhy">
      <span className="sg-hwhy__label">{competitor ? "What it tells you" : "Why it matters"}</span>
      <span className="sg-hwhy__text">{why.text}</span>
      {why.action && <span className="sg-hwhy__action">{why.action}</span>}
    </div>
  ) : null;

// "Also reach: <persona>" opens the people card.
export { AlsoReach } from "./PeopleCard";

/** A competitor's own hiring: a quiet coral tag next to the account. */
export const CompetitorTag = () => <span className="sg-ctag">Competitor</span>;

/** A recruiter posted it: who, said quietly, since the persona is the one to reach. */
/** A poster who is not the one to reach: a recruiter ("Talent Acquisition") or a peer (their own title). */
export const posterLine = (poster, peer) =>
  `Posted by ${poster.name}${peer ? (poster.title ? `, ${poster.title}` : "") : ", Talent Acquisition"}`;

export const PostedBy = ({ poster, peer = false }) =>
  poster ? (
    <span className="sg-hposter">
      <ContactAvatar photo={poster.photoUrl} name={poster.name} />
      <span>{posterLine(poster, peer)}</span>
    </span>
  ) : null;
